import { ReactNode, useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { MarkdownEditor } from "~/components/ui/markdown-editor";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import { cn } from "~/lib/utils";
import { SectionHeading } from "~/pages/manifold-nextgen/components/SectionHeading";

interface EditableDescriptionProps {
  value?: string | null;
  canEdit: boolean;
  // Persist the new description. Should throw on failure so the dialog stays
  // open; the caller owns the API call + success/error toasts.
  onSave: (next: string) => Promise<void>;
  // Titles the block with a rule and a standing Edit button.
  heading?: string;
  // Renders the pencil inline next to the text (still hover-to-reveal), instead
  // of the default icon pinned to the top-right corner.
  inlineIcon?: boolean;
}

/**
 * Markdown description block shared by the project / dataset / agent overview
 * pages: renders the description, and for editors opens a Markdown editor
 * dialog.
 */
export function EditableDescription({
  value,
  canEdit,
  onSave,
  heading,
  inlineIcon = false,
}: EditableDescriptionProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const text = value ?? "";
  const hasValue = Boolean(text.trim());

  const openEdit = () => {
    setDraft(text);
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditOpen(false);
    } catch {
      // Caller surfaces the error toast; keep the dialog open so edits aren't lost.
    } finally {
      setSaving(false);
    }
  };

  const empty = <p className="text-sm italic text-muted-foreground">No description provided.</p>;

  const hoverPencil = (className?: string) =>
    canEdit && (
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          "size-7 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100",
          className
        )}
        aria-label="Edit description"
        onClick={openEdit}
      >
        <Pencil className="size-3.5" />
      </Button>
    );

  const dialog = (
    <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
      <DialogContent
        className="flex max-h-[90vh] flex-col sm:max-w-2xl"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Edit description</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          <MarkdownEditor
            aria-label="Description"
            value={draft}
            onChange={setDraft}
            autoFocus
            readOnly={saving}
            paneClassName="min-h-[16rem] max-h-[50vh] overflow-y-auto"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  let body: ReactNode;
  if (heading) {
    body = (
      <div className="flex flex-col gap-3">
        <SectionHeading
          title={heading}
          action={
            canEdit && (
              // "Edit" is enough beside the heading; the accessible name is not.
              <Button
                variant="outline"
                size="sm"
                aria-label={`Edit ${heading.toLowerCase()}`}
                onClick={openEdit}
              >
                <Pencil className="size-3.5" />
                Edit
              </Button>
            )
          }
        />
        {hasValue ? <MarkdownPreview>{text}</MarkdownPreview> : empty}
      </div>
    );
  } else if (inlineIcon) {
    body = (
      <div className="flex items-center gap-1.5">
        {hasValue ? <MarkdownPreview className="max-w-2xl">{text}</MarkdownPreview> : empty}
        {hoverPencil("shrink-0")}
      </div>
    );
  } else if (hasValue) {
    body = (
      <>
        <MarkdownPreview className={cn(canEdit && "pr-8")}>{text}</MarkdownPreview>
        {hoverPencil("absolute right-0 top-0")}
      </>
    );
  } else {
    body = (
      <div className="flex items-center gap-1">
        {empty}
        {hoverPencil()}
      </div>
    );
  }

  return (
    <div className="group relative">
      {body}
      {dialog}
    </div>
  );
}
